import { XGrid } from '@AMIEWEB/Common/XGrid/XGrid';
import { formatDate } from '@AMIEWEB/Order/OrderDetails/helper';
import { GridSortModel } from '@mui/x-data-grid-pro';
import { Grid } from '@mui/material';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import { missingField } from 'app/constants';
import { ITimeOffRequests, gridTags, modsEnums } from 'app/models/Placement/PlacementMods';
import { differenceInDays, isBefore, isValid } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePromiseTracker } from 'react-promise-tracker';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCurrentModDetails,
  selectExpandedModsDialog,
  selectIsEditExistingMod,
} from 'store/redux-store/placement-mods/selectors';
import { placementModsAction } from 'store/redux-store/placement-mods/slice';
import { makeStyles } from 'tss-react/mui';
import { isNumber } from '../../ModificationsUtils';
import { getAssignedTimeOffGridColumns } from './ultils';

const expandedDetailPanelStyles = makeStyles()({
  expandedGrid: {
    fontSize: '14px',
    '& .MuiDataGrid-main': {
      minHeight: '60px',
    },
  },
});
export const AssignTimeOffGrid = ({
  timeOffRequests,
  totalCount,
  assignedCount,
  updateAssignedField,
}: {
  timeOffRequests: ITimeOffRequests[];
  totalCount: number;
  assignedCount: number;
  updateAssignedField: Function;
}) => {
  const { t } = useTranslation();
  const isExpanded = useSelector(selectExpandedModsDialog);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [columns, setColumns] = useState<any>(getAssignedTimeOffGridColumns(t, isExpanded, updateAssignedField));
  const dispatch = useDispatch();
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const { promiseInProgress: isCallingAPI } = usePromiseTracker({ area: 'mod-details-api', delay: 0 });
  const selectedModsDetails = useSelector(selectCurrentModDetails);
  const isEditExistingMod = useSelector(selectIsEditExistingMod);

  const { classes } = expandedDetailPanelStyles();

  useEffect(() => {
    const initialColumns = getAssignedTimeOffGridColumns(t, isExpanded, updateAssignedField);
    setColumns(initialColumns);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, isExpanded]);

  const handleRefreshGrid = () => {
    setSortModel([]);
    dispatch(
      placementModsAction.getTravelAllowanceModDetailAction({
        placementId: selectedModsDetails.placementId,
        travelerOptionId: selectedModsDetails.travelerOptionId,
        modEnum: modsEnums.InterimTravel,
        newMod: !isEditExistingMod,
      }),
    );
    dispatch(placementModsAction.setGridModAltered(false));
  };

  const resetColumns = () => {
    setColumns(getAssignedTimeOffGridColumns(t, isExpanded, updateAssignedField));
  };
  const sortChangeHandler = (model: GridSortModel) => {
    setSortModel(model);
  };
  const getFormattedInterimTimeOff = (timeOffRequestsParam: ITimeOffRequests[]) => {
    return timeOffRequestsParam?.map((item, index) => {
      const data = {
        id: `${item.placementId}-${index}`,
        placementId: item.placementId,
        travelerOptionId: item.travelerOptionId,
        placementTimeOffId: item.placementTimeOffId,
        startDate: item?.startDate ? formatDate(item?.startDate) : missingField,
        endDate: item?.endDate ? formatDate(item?.endDate) : missingField,
        days:
          !isValid(new Date(item?.startDate)) ||
          !isValid(new Date(item?.endDate)) ||
          isBefore(new Date(item?.endDate), new Date(item?.startDate))
            ? missingField
            : differenceInDays(new Date(item?.endDate), new Date(item?.startDate)) + 1,
        hours: isNumber(item.hours) ? item.hours : missingField,
        cost: item.cost ? item.cost.toString() : missingField,
        placementTimeOffPayerId: item.placementTimeOffPayerId,
        timestamp: item.timestamp,
        isModified: item.isModified,
        facilityApprovedBy: item.facilityApprovedBy ? item.facilityApprovedBy : missingField,
        interimPlacementId: item.interimPlacementId,
        interimTravelerOptionId: item.interimTravelerOptionId,
        timeOffPayerDescription: item.timeOffPayerDescription ? item.timeOffPayerDescription : missingField,
        placementTimeStamp: item.placementTimeStamp,
        approvalStatusId: item.approvalStatusId,
        voidStatus: item.voidStatus,
        assigned: item?.assigned,
      };
      return data;
    });
  };
  const AssignedTimeOffGrid = React.useMemo(() => {
    return (
      <Grid>
        <XGrid
          gridTag={gridTags.INTERIMGRIDTAG}
          rows={getFormattedInterimTimeOff(timeOffRequests) || []}
          columns={columns}
          disableSelectionOnClick
          pagination={false}
          autoHeight={true}
          sx={{ minWidth: '500px' }}
          sortModel={sortModel}
          onSortModelChange={sortChangeHandler}
          hideFooter
          inDialog
          title={t('placement.placementMods.timeOffRequest')}
          secondaryTitle={` / ${t('placement.placementMods.assigned')} (${assignedCount})`}
          rowCountDisplay={totalCount}
          loading={isCallingAPI}
          className={classes.expandedGrid}
          toolbarProps={{
            refreshButton: true,
            columnSelectorProps: {
              fallbackPlacements: [
                'bottom-end',
                'bottom',
                'bottom-start',
                'top-start',
                'top',
                'top-end',
                'left',
                'right',
              ],
            },
            resetColumns: () => {
              resetColumns();
              dispatch(
                globalActions.setSnackBar({
                  message: 'Columns have been reset.',
                  severity: 'success',
                }),
              );
            },
            refreshGrid: () => {
              handleRefreshGrid();
            },
          }}
        />
      </Grid>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeOffRequests, isExpanded, isCallingAPI, resetColumns, columns, selectedModsDetails]);

  return <>{AssignedTimeOffGrid}</>;
};
